import type { ChatNotice } from './domain.js';

// Pure formatting for the dynamic-knowledge block (C6) — no I/O, unit-tested.
// Output is FRENCH (the KB's content language; the responder answers in the
// guest's language from FR source) and is injected as an UNCACHED system
// block after the baseline KB, which it overrides.

/** Public menu_feed row fields the live context consumes (daily rows). */
export interface MenuFeedItem {
  name: string;
  description: string | null;
  price: number | null;
  is_course_note: boolean;
  course_rank: number | null;
  sort_order: number | null;
}

/** DD/MM/YYYY in the hotel's timezone (guests and staff both think in Paris dates). */
function frDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
}

/** "Du 10/07/2026 au 14/07/2026 : " — the notice's validity window, when set. */
export function noticeWindowPrefix(notice: Pick<ChatNotice, 'startsAt' | 'endsAt'>): string {
  if (notice.startsAt && notice.endsAt) {
    return `Du ${frDate(notice.startsAt)} au ${frDate(notice.endsAt)} : `;
  }
  if (notice.endsAt) return `Jusqu'au ${frDate(notice.endsAt)} : `;
  if (notice.startsAt) return `À partir du ${frDate(notice.startsAt)} : `;
  return '';
}

function menuLine(item: MenuFeedItem): string {
  const description = item.description?.trim() ? ` — ${item.description.trim()}` : '';
  const price = item.price !== null && !item.is_course_note ? ` (${item.price} €)` : '';
  return `- ${item.name}${description}${price}`;
}

/**
 * Assemble the volatile FR knowledge block: active notices (operational,
 * highest authority), learned facts (the runtime KB tier), and today's Menu
 * du Jour (live from the menu domain). '' when nothing is active.
 */
export function formatLiveContext(input: {
  notices: ChatNotice[];
  menu: MenuFeedItem[];
}): string {
  const sections: string[] = [];

  const operational = input.notices.filter((n) => n.kind === 'notice');
  if (operational.length > 0) {
    sections.push(
      '## Annonces en cours (prioritaires)\n' +
        operational.map((n) => `- ${noticeWindowPrefix(n)}${n.bodyFr}`).join('\n'),
    );
  }

  const facts = input.notices.filter((n) => n.kind === 'fact');
  if (facts.length > 0) {
    sections.push(
      '## Compléments récents à la base\n' + facts.map((n) => `- ${n.bodyFr}`).join('\n'),
    );
  }

  if (input.menu.length > 0) {
    const ordered = [...input.menu].sort(
      (a, b) =>
        (a.course_rank ?? 0) - (b.course_rank ?? 0) || (a.sort_order ?? 0) - (b.sort_order ?? 0),
    );
    sections.push("## Menu du jour (aujourd'hui)\n" + ordered.map(menuLine).join('\n'));
  }

  return sections.join('\n\n');
}
